import React from 'react'
import { useWatch } from 'react-hook-form'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import ControlledTextInput from 'transfix-form/components/ControlledTextInput'
import { ClockIcon } from 'transfix-icons/build'
import { Grid, makeStyles } from 'transfix-ui/core'
import {
  TIME_WINDOW_FIELD_OPTIONS,
  TIME_WINDOW_OPERATOR_OPTIONS,
  TIME_WINDOW_UNIT_OPTIONS,
} from '../constants'
import { getTimeWindowOptionsByUnit } from '../utils'
import { useIssueRuleFormContext, getErrorMessage } from './RuleForm'
import TriggerCard from './TriggerCard'

const useStyles = makeStyles(() => ({
  fullRow: {
    marginRight: '50%',
  },
}))

interface ITimeWindowCardProps {
  onAddTrigger: () => void
  isAddTriggerDisabled: boolean
  onRemove: () => void
  showClearButton: boolean
  onCancel: () => void
  index: number
}

const TimeWindowCard = ({
  index,
  isAddTriggerDisabled,
  onAddTrigger,
  onCancel,
  onRemove,
  showClearButton,
}: ITimeWindowCardProps) => {
  const classes = useStyles()
  const { control, errors, setValue } = useIssueRuleFormContext()

  const timeWindowUnit = useWatch<string>({ control, name: `nodes[${index}].timeWindowUnit` })
  const timeWindowAmountOptions = getTimeWindowOptionsByUnit(timeWindowUnit || '')
  const timeWindowUnitIsHours = timeWindowUnit === 'hours'

  const handleChangeUnit = () => {
    setValue(`nodes[${index}].timeWindowTimeAmount`, undefined)
  }

  return (
    <TriggerCard
      icon={<ClockIcon className='inline' size='xlarge' color='skyBlue6' />}
      isAddTriggerDisabled={isAddTriggerDisabled}
      onAddTrigger={onAddTrigger}
      onCancel={onCancel}
      onRemove={onRemove}
      showClearButton={showClearButton}
      title='Time Window'>
      <Grid container spacing={2}>
        <Grid item md={12} lg={6} className={classes.fullRow}>
          <ControlledSelect
            control={control}
            name={`nodes[${index}].timeWindowOperator`}
            SelectProps={{
              options: TIME_WINDOW_OPERATOR_OPTIONS,
              error: !!getErrorMessage(errors, `nodes[${index}].timeWindowOperator`),
              validationMessage: getErrorMessage(errors, `nodes[${index}].timeWindowOperator`),
            }}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <ControlledSelect
            control={control}
            name={`nodes[${index}].timeWindowUnit`}
            onChange={handleChangeUnit}
            SelectProps={{
              options: TIME_WINDOW_UNIT_OPTIONS,
              error: !!getErrorMessage(errors, `nodes[${index}].timeWindowUnit`),
              validationMessage: getErrorMessage(errors, `nodes[${index}].timeWindowUnit`),
            }}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          {!timeWindowUnitIsHours && (
            <ControlledSelect
              control={control}
              name={`nodes[${index}].timeWindowTimeAmount`}
              SelectProps={{
                options: timeWindowAmountOptions,
                error: !!getErrorMessage(errors, `nodes[${index}].timeWindowTimeAmount`),
                validationMessage: getErrorMessage(errors, `nodes[${index}].timeWindowTimeAmount`),
                disabled: !timeWindowUnit,
              }}
            />
          )}
          {timeWindowUnitIsHours && (
            <ControlledTextInput
              control={control}
              name={`nodes[${index}].timeWindowTimeAmount`}
              TextInputProps={{
                type: 'number',
                error: !!getErrorMessage(errors, `nodes[${index}].timeWindowTimeAmount`),
                validationMessage: getErrorMessage(errors, `nodes[${index}].timeWindowTimeAmount`),
                disabled: !timeWindowUnit,
              }}
            />
          )}
        </Grid>
        <Grid item xs={12} lg={12}>
          <ControlledSelect
            control={control}
            name={`nodes[${index}].timeWindowField`}
            SelectProps={{
              options: TIME_WINDOW_FIELD_OPTIONS,
              error: !!getErrorMessage(errors, `nodes[${index}].timeWindowField`),
              validationMessage: getErrorMessage(errors, `nodes[${index}].timeWindowField`),
            }}
          />
        </Grid>
      </Grid>
    </TriggerCard>
  )
}

export default TimeWindowCard
